import { intentLabel, playbookLabel, sampleLabel } from "@/lib/product-labels";

export const SINGLE_STAGE_RUN_RECORDS_STORAGE_KEY = "wentao.admin.single_stage_run_records.v1";
export const SINGLE_STAGE_RUN_RECORDS_EVENT = "wentao:single-stage-run-records-updated";

const MAX_SINGLE_STAGE_RECORDS = 100;

export type SingleStageRuntimeKind =
	| "intent"
	| "playbook"
	| "tools"
	| "knowledge"
	| "prompt"
	| "quality";

export type SingleStageRuntimeTargetStage =
	| "intent_classification"
	| "playbook_routing"
	| "tool_and_evidence"
	| "knowledge_retrieval"
	| "prompt_response"
	| "quality_guard";

export type SingleStageRunStatus = "success" | "partial" | "error";

export type SingleStageRunSummaryItem = {
	label: string;
	value: string;
};

export type SingleStageRuntimeResultSnapshot = {
	title: string;
	status: SingleStageRunStatus;
	summary: SingleStageRunSummaryItem[];
	raw: unknown;
};

export type SingleStageRunRecord = {
	id: string;
	kind: "single_stage";
	runKind: SingleStageRuntimeKind;
	targetStage: SingleStageRuntimeTargetStage;
	targetStageLabel: string;
	sampleId: string;
	message: string;
	expectedIntent: string;
	expectedPlaybook: string;
	actualIntent: string;
	actualPlaybook: string;
	merchantId: string;
	customerId: string;
	assetName: string;
	boundary: string;
	status: SingleStageRunStatus;
	title: string;
	summary: SingleStageRunSummaryItem[];
	raw: unknown;
	traceId: string | null;
	runId: string | null;
	sessionId: string | null;
	createdAt: string;
	updatedAt: string;
	searchable: string;
};

export const SINGLE_STAGE_TARGET_STAGE_LABELS: Record<SingleStageRuntimeTargetStage, string> = {
	intent_classification: "意图分类",
	playbook_routing: "处理方案",
	tool_and_evidence: "工具与证据",
	knowledge_retrieval: "知识检索",
	prompt_response: "回复生成",
	quality_guard: "质量策略",
};

export function createSingleStageRunRecord({
	runKind,
	targetStage,
	sampleId,
	message,
	expectedIntent,
	expectedPlaybook,
	assetName,
	boundary,
	merchantId,
	customerId = "模拟客户",
	result,
}: {
	runKind: SingleStageRuntimeKind;
	targetStage: SingleStageRuntimeTargetStage;
	sampleId: string;
	message: string;
	expectedIntent: string;
	expectedPlaybook: string;
	assetName: string;
	boundary: string;
	merchantId: string;
	customerId?: string;
	result: SingleStageRuntimeResultSnapshot;
}): SingleStageRunRecord {
	const now = new Date().toISOString();
	const metadata = extractRunMetadata(result);
	const actualIntent =
		metadata.actualIntent || summaryValue(result.summary, "实际分类") || expectedIntent || "未返回";
	const actualPlaybook =
		metadata.actualPlaybook ||
		summaryValue(result.summary, "命中方案") ||
		expectedPlaybook ||
		"未返回";
	const targetStageLabel = SINGLE_STAGE_TARGET_STAGE_LABELS[targetStage];
	const id = createRecordId(targetStage, sampleId);
	const searchable = [
		id,
		runKind,
		targetStage,
		targetStageLabel,
		sampleId,
		message,
		expectedIntent,
		expectedPlaybook,
		intentLabel(expectedIntent),
		playbookLabel(expectedPlaybook),
		actualIntent,
		actualPlaybook,
		intentLabel(actualIntent),
		playbookLabel(actualPlaybook),
		sampleLabel(sampleId),
		merchantId,
		customerId,
		assetName,
		result.title,
		result.summary.map((item) => `${item.label}:${item.value}`).join(" "),
	]
		.join(" ")
		.toLowerCase();

	return {
		id,
		kind: "single_stage",
		runKind,
		targetStage,
		targetStageLabel,
		sampleId,
		message,
		expectedIntent,
		expectedPlaybook,
		actualIntent,
		actualPlaybook,
		merchantId,
		customerId,
		assetName,
		boundary,
		status: result.status,
		title: result.title,
		summary: result.summary,
		raw: result.raw,
		traceId: metadata.traceId,
		runId: metadata.runId,
		sessionId: metadata.sessionId,
		createdAt: now,
		updatedAt: now,
		searchable,
	};
}

export function readSingleStageRunRecords(): SingleStageRunRecord[] {
	if (!canUseLocalStorage()) return [];
	try {
		const raw = window.localStorage.getItem(SINGLE_STAGE_RUN_RECORDS_STORAGE_KEY);
		if (!raw) return [];
		const parsed = JSON.parse(raw) as unknown;
		if (!Array.isArray(parsed)) return [];
		return parsed.filter(isSingleStageRunRecord).slice(0, MAX_SINGLE_STAGE_RECORDS);
	} catch {
		return [];
	}
}

export function saveSingleStageRunRecord(record: SingleStageRunRecord) {
	if (!canUseLocalStorage()) return;
	const existing = readSingleStageRunRecords();
	const next = [record, ...existing.filter((item) => item.id !== record.id)].slice(
		0,
		MAX_SINGLE_STAGE_RECORDS,
	);
	window.localStorage.setItem(SINGLE_STAGE_RUN_RECORDS_STORAGE_KEY, JSON.stringify(next));
	window.dispatchEvent(new CustomEvent(SINGLE_STAGE_RUN_RECORDS_EVENT));
}

function extractRunMetadata(result: SingleStageRuntimeResultSnapshot): {
	traceId: string | null;
	runId: string | null;
	sessionId: string | null;
	actualIntent: string;
	actualPlaybook: string;
} {
	const raw = asRecord(result.raw);
	const nestedIntent = asRecord(raw?.intent);
	const messagesResponse = asRecord(raw?.messages_response);
	const responseIntent = asRecord(messagesResponse?.intent);
	const dispatchPlaybook = asRecord(raw?.recommended_playbook);
	return {
		traceId: stringOrNull(raw?.trace_id ?? raw?.traceId),
		runId: stringOrNull(raw?.run_id ?? raw?.runId),
		sessionId: stringOrNull(raw?.session_id ?? raw?.sessionId ?? messagesResponse?.session_id),
		actualIntent: firstString(
			raw?.intent,
			nestedIntent?.intent,
			responseIntent?.intent,
			messagesResponse?.actual_intent,
		),
		actualPlaybook: firstString(
			raw?.recommended_playbook_id,
			dispatchPlaybook?.id,
			messagesResponse?.recommended_playbook_id,
		),
	};
}

function isSingleStageRunRecord(value: unknown): value is SingleStageRunRecord {
	const record = asRecord(value);
	return (
		record?.kind === "single_stage" &&
		typeof record.id === "string" &&
		typeof record.targetStageLabel === "string" &&
		typeof record.updatedAt === "string" &&
		Array.isArray(record.summary)
	);
}

function summaryValue(summary: SingleStageRunSummaryItem[], label: string): string {
	return summary.find((item) => item.label === label)?.value ?? "";
}

function createRecordId(targetStage: SingleStageRuntimeTargetStage, sampleId: string): string {
	const timestamp = Date.now();
	const random = Math.random().toString(36).slice(2, 7);
	const cleanSampleId = sampleId.replace(/[^a-zA-Z0-9_-]/g, "_");
	return `ssr_${targetStage}_${cleanSampleId}_${timestamp}_${random}`;
}

function canUseLocalStorage() {
	return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function asRecord(value: unknown): Record<string, unknown> | null {
	if (value && typeof value === "object" && !Array.isArray(value)) {
		return value as Record<string, unknown>;
	}
	return null;
}

function stringOrNull(value: unknown): string | null {
	if (typeof value === "string" && value.trim().length > 0) return value;
	return null;
}

function firstString(...values: unknown[]): string {
	for (const value of values) {
		if (typeof value === "string" && value.trim().length > 0) return value;
		if (typeof value === "number" || typeof value === "boolean") return String(value);
	}
	return "";
}
